import { createSlice } from '@reduxjs/toolkit'
import { fetchCardData } from 'entities/Card'
import { randomColor } from 'shared/lib/randomColor/randomColor'

const momentStatusKeys = {
    accepted: 'acceptedMoments',
    wanted: 'wantedMoments',
    rejected: 'rejectedMoments',
}

const initialState = {
    uid: undefined,
    acceptedMoments: [],
    wantedMoments: [],
    rejectedMoments: [],
    createdMoments: [],
    pinnedMoments: [],
    allCards: [],
    cards: [],
    status: null,
    error: null,
    direction: [0, 0],
    arrayCard: [],
    isLoaded: true,
    activityPending: false,
    activityError: null,
    id: undefined,
    name: '',
    avatar: '',
    descr: '',
    nickname: '',
    background: '',
    friends: [],
    isAdmin: false,
    telegram: null,
    limitationCount: 15,
    limitationTime: null,
    isPremium: false,
}

const advanceCardQueue = (state, { direction }) => {
    state.arrayCard.pop()
    if (state.cards.length !== 0) {
        state.arrayCard.unshift(...state.cards.splice(0, 1))
        if (!state.arrayCard[0].color) {
            state.arrayCard[0].color = randomColor()
        }
    }
    state.direction = direction
}

const replaceMomentStatus = (state, payload) => {
    const { cardId, status, occurredAt } = payload

    Object.values(momentStatusKeys).forEach((key) => {
        state[key] = state[key].filter((item) => item.id !== cardId)
    })
    state[momentStatusKeys[status]].push({ id: cardId, time: occurredAt })

    if (status !== 'wanted') {
        state.pinnedMoments = state.pinnedMoments.filter(
            (item) => item.id !== cardId
        )
    }
    if (payload.limitationCount !== undefined) {
        state.limitationCount = payload.limitationCount
    }
    if (payload.limitationTime !== undefined) {
        state.limitationTime = payload.limitationTime
    }
}

const authDataSlice = createSlice({
    name: 'authData',
    initialState,
    reducers: {
        setUid(state, action) {
            state.uid = action.payload
        },
        setSignedOut(state) {
            state.uid = null
        },
        setUserData(state, action) {
            state.acceptedMoments = action.payload.acceptedMoments
            state.wantedMoments = action.payload.wantedMoments
            state.rejectedMoments = action.payload.rejectedMoments
            state.pinnedMoments = action.payload.pinnedMoments

            state.id = action.payload.id
            state.name = action.payload.name
            state.avatar = action.payload.avatar
            state.descr = action.payload.descr
            state.nickname = action.payload.nickname
            state.background = action.payload.background
            state.friends = action.payload.friends
            state.isAdmin = action.payload.admin
            state.telegram = action.payload.telegram
            state.isPremium = action.payload.isPremium

            state.limitationCount = action.payload.limitationCount
            state.limitationTime = action.payload.limitationTime
        },
        applyMomentChoice(state, action) {
            advanceCardQueue(state, action.payload)
            if (action.payload.card.id !== 0) {
                replaceMomentStatus(state, {
                    ...action.payload,
                    cardId: action.payload.card.id,
                })
            }
        },
        restoreMomentChoice(state, action) {
            const snapshot = action.payload
            state.arrayCard = snapshot.arrayCard
            state.cards = snapshot.cards
            state.direction = snapshot.direction
            state.acceptedMoments = snapshot.acceptedMoments
            state.wantedMoments = snapshot.wantedMoments
            state.rejectedMoments = snapshot.rejectedMoments
            state.pinnedMoments = snapshot.pinnedMoments
            state.limitationCount = snapshot.limitationCount
            state.limitationTime = snapshot.limitationTime
        },
        applyMomentStatus(state, action) {
            replaceMomentStatus(state, action.payload)
        },
        applyPin(state, action) {
            const { cardId, shouldPin, occurredAt } = action.payload
            state.pinnedMoments = state.pinnedMoments.filter(
                (item) => item.id !== cardId
            )
            if (shouldPin) {
                state.pinnedMoments.push({ id: cardId, time: occurredAt })
            }
        },
        activityStarted(state) {
            state.activityPending = true
            state.activityError = null
        },
        activityFinished(state) {
            state.activityPending = false
        },
        activityFailed(state, action) {
            state.activityError = action.payload
        },
        isLoaded(state) {
            state.isLoaded = true
        },
        updateAvatar(state, action) {
            state.avatar = action.payload
        },
        updateBackground(state, action) {
            state.background = action.payload
        },
        updateSettings(state, action) {
            state.name = action.payload[0]
            state.nickname = action.payload[1]
            state.descr = action.payload[2]
            state.telegram = action.payload[3]
        },
        setPremium(state, action) {
            state.isPremium = Boolean(action.payload)
        },
        addFriend(state, action) {
            state.friends.push(action.payload)
        },
        deleteFriend(state, action) {
            state.friends = state.friends.filter(
                (item) => item !== action.payload
            )
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCardData.pending, (state) => {
                state.status = 'loading'
                state.error = null
            })
            .addCase(fetchCardData.fulfilled, (state, action) => {
                state.allCards = action.payload

                for (let i = 0; i < state.allCards.length; i++) {
                    if (!state.allCards[i].color) {
                        state.allCards[i].color = randomColor()
                    }
                }

                state.createdMoments = state.allCards.filter(
                    (item) =>
                        item.userId === state.id && item.userId !== undefined
                )

                const usedIds = state.acceptedMoments
                    .concat(state.rejectedMoments, state.wantedMoments)
                    .map((item) => item.id)
                const filteredCardData = state.allCards
                    .filter((item) => !usedIds.includes(item.id))
                    .sort(() => Math.random() - 0.5)

                state.cards = filteredCardData
                state.arrayCard = filteredCardData
                    .splice(0, 3)
                    .slice()
                    .reverse()
                state.status = 'resolved'
            })
            .addCase(fetchCardData.rejected, () => {
                throw new Error('Error from fetchCardData')
            })
    },
})

export default authDataSlice.reducer
export const { actions: authActions } = authDataSlice
